import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

// Hilfsfunktion zur Generierung von iCal-Dateien
const generateICS = (events, filename = "event-schedule.ics") => {
  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const pad = (n) => String(n).padStart(2, "0");
    // Nutze lokale Zeit ohne Zeitzonen-Konvertierung, um Tagesverschiebungen zu vermeiden
    return (
      `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}` +
      `T${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
    );
  };

  const escapeText = (text) => {
    return text
      .replace(/\\/g, "\\\\")
      .replace(/;/g, "\\;")
      .replace(/,/g, "\\,")
      .replace(/\n/g, "\\n");
  };

  let icsContent = "BEGIN:VCALENDAR\r\n";
  icsContent += "VERSION:2.0\r\n";
  icsContent += "PRODID:-//Chin Woo World Championship//Event Schedule//EN\r\n";
  icsContent += "CALSCALE:GREGORIAN\r\n";
  icsContent += "METHOD:PUBLISH\r\n";

  events.forEach((event, index) => {
    if (!event.startISO && !event.endISO) return; // Überspringe Events ohne Zeitangabe

    const startDate = formatDate(event.startISO || event.endISO);
    const endDate = formatDate(event.endISO || event.startISO);
    
    if (!startDate) return;

    // Standard-Dauer von 1 Stunde, falls kein Enddatum vorhanden
    const end = endDate || formatDate(
      new Date(new Date(event.startISO).getTime() + 60 * 60 * 1000).toISOString()
    );

    icsContent += "BEGIN:VEVENT\r\n";
    icsContent += `UID:${Date.now()}-${index}@chinwoo-worldchampionship.org\r\n`;
    icsContent += `DTSTART:${startDate}\r\n`;
    icsContent += `DTEND:${end}\r\n`;
    icsContent += `DTSTAMP:${formatDate(new Date().toISOString())}\r\n`;
    icsContent += `SUMMARY:${escapeText(event.title)}\r\n`;
    icsContent += `DESCRIPTION:${escapeText(event.description)}\r\n`;
    icsContent += `LOCATION:${escapeText(event.location)}\r\n`;
    icsContent += "STATUS:CONFIRMED\r\n";
    icsContent += "SEQUENCE:0\r\n";
    icsContent += "END:VEVENT\r\n";
  });

  icsContent += "END:VCALENDAR\r\n";

  // Erkenne iOS-Geräte für bessere Kompatibilität
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  if (isIOS) {
    // Auf iOS: Verwende data URI, der direkt die Kalender-App öffnet
    const dataUri = `data:text/calendar;charset=utf-8,${encodeURIComponent(icsContent)}`;
    const link = document.createElement("a");
    link.href = dataUri;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } else {
    // Auf anderen Geräten: Standard-Download
    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  }
};

// Extrahierte Timeline-Item Komponente
const TimelineItem = React.memo(
  ({
    date,
    title,
    description,
    location,
    startISO,
    endISO,
    onExport,
    t,
  }) => {
  const formatLocation = (loc) => {
    if (loc === "Sparkassen-Arena") {
      return (
        <>
          <img
            src={`${process.env.PUBLIC_URL}/images/sponsors/sparkasse.png`}
            alt={t("eventLocation.sponsorLogoAlt")}
            width={80}
            height={20}
            loading="lazy"
            decoding="async"
            className="mr-0.5 inline-block h-5 w-auto max-h-5 align-text-bottom object-contain object-left drop-shadow-sm"
          />
          <span>-Arena</span>
        </>
      );
    }
    return loc;
  };

  const canExport = Boolean(startISO || endISO);

  return (
    <div className="grid grid-cols-[2.5rem_1fr] items-start gap-x-3 md:grid-cols-[2.75rem_1fr] md:gap-x-4">
      <div className="flex justify-center pt-1.5 md:pt-2">
        <span
          className="relative z-10 size-3.5 shrink-0 rounded-full border-[3px] border-white bg-primary shadow-sm motion-safe:transition-transform motion-safe:hover:scale-110 dark:border-slate-900 dark:bg-primary-light md:size-4 md:border-[3px]"
          aria-hidden
        />
      </div>

      <div className="flex min-w-0 flex-col gap-2">
        <div>
          <span className="inline-block rounded-full border border-slate-200/80 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary dark:border-slate-600 dark:bg-primary-light/15 dark:text-primary-light">
            {date}
          </span>
        </div>

        <div className="group/card rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:shadow-md motion-safe:hover:-translate-y-0.5 dark:border-slate-700 dark:bg-slate-800 md:p-7">
          <div className="mb-4 flex items-start justify-between gap-3">
            <h3
              className="min-w-0 flex-1 break-words text-lg font-semibold leading-snug text-primary dark:text-primary-light md:text-xl"
              style={{
                hyphens: "auto",
                WebkitHyphens: "auto",
                msHyphens: "auto",
              }}
            >
              {title.replace(
                "Wertungsrichtereinweisung",
                "Wertungsrichter\u00ADeinweisung"
              )}
            </h3>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (canExport) {
                  onExport(
                    [{ title, description, location, startISO, endISO }],
                    `${title.replace(/\s+/g, "-")}.ics`
                  );
                }
              }}
              disabled={!canExport}
              className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-white transition-all duration-300 motion-safe:group-hover/card:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/45 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-primary-light/45 dark:focus-visible:ring-offset-slate-800 ${
                canExport
                  ? "cursor-pointer bg-primary shadow-sm hover:bg-primary-dark hover:shadow-md dark:bg-primary-light dark:hover:bg-primary"
                  : "cursor-not-allowed bg-slate-300 opacity-60 dark:bg-slate-600"
              }`}
              title={
                canExport
                  ? t("button.export_calendar") || "Event zum Kalender hinzufügen"
                  : t("eventSchedule.no_time_available") ||
                    "Keine Zeitangabe verfügbar"
              }
            >
              <i className="fas fa-calendar-plus text-sm" aria-hidden />
            </button>
          </div>

          <p className="mb-4 leading-relaxed text-slate-600 dark:text-slate-400">
            {description}
          </p>

          <div className="flex items-center gap-2 border-t border-slate-200 pt-4 text-slate-600 dark:border-slate-700 dark:text-slate-400">
            <span
              className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-primary-light/15 dark:text-primary-light"
              aria-hidden
            >
              <i className="fas fa-map-marker-alt text-sm" />
            </span>
            <span className="min-w-0 text-sm leading-snug md:text-base">
              {formatLocation(location)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
  }
);

TimelineItem.propTypes = {
  date: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  startISO: PropTypes.string,
  endISO: PropTypes.string,
  onExport: PropTypes.func.isRequired,
};

function EventSchedule() {
  const { t } = useTranslation();
  const [showAdditionalDays, setShowAdditionalDays] = useState(false);

  // Zusammenfassen der Timeline-Daten mit useMemo
  const allTimelineItems = useMemo(() => {
    const days = [1, 2, 3, 4, 5];
    return days.flatMap((day) => {
      const dayKey = `day${day}`;
      const events = [];
      let eventIndex = 1;
      let eventKey = `event${eventIndex}`;

      while (
        t(`eventSchedule.${dayKey}.${eventKey}.title`, {
          returnObjects: true,
        }) !== `eventSchedule.${dayKey}.${eventKey}.title`
      ) {
        events.push({
          date: t(`eventSchedule.${dayKey}.${eventKey}.dateDisplay`),
          title: t(`eventSchedule.${dayKey}.${eventKey}.title`),
          description: t(`eventSchedule.${dayKey}.${eventKey}.description`),
          location: t(`eventSchedule.${dayKey}.${eventKey}.location`),
          startISO: t(`eventSchedule.${dayKey}.${eventKey}.startISO`, { returnObjects: true }) !== `eventSchedule.${dayKey}.${eventKey}.startISO` 
            ? t(`eventSchedule.${dayKey}.${eventKey}.startISO`) 
            : null,
          endISO: t(`eventSchedule.${dayKey}.${eventKey}.endISO`, { returnObjects: true }) !== `eventSchedule.${dayKey}.${eventKey}.endISO`
            ? t(`eventSchedule.${dayKey}.${eventKey}.endISO`)
            : null,
        });
        eventIndex++;
        eventKey = `event${eventIndex}`;
      }
      return events;
    });
  }, [t]);

  const mainItems = useMemo(
    () => allTimelineItems.slice(0, 3),
    [allTimelineItems]
  );

  const allDisplayedItems = showAdditionalDays 
    ? allTimelineItems 
    : mainItems;

  const handleExportAll = () => {
    const eventsWithDates = allTimelineItems.filter(event => event.startISO || event.endISO);
    if (eventsWithDates.length > 0) {
      generateICS(eventsWithDates, "chinwoo-worldchampionship-schedule.ics");
    }
  };

  const handleExportSingle = (events, filename) => {
    generateICS(events, filename);
  };

  const exportableCount = allTimelineItems.filter(
    (event) => event.startISO || event.endISO
  ).length;

  return (
    <section
      className="relative z-10 bg-slate-50/50 pt-12 pb-12 transition-colors dark:bg-slate-900"
      id="schedule"
      aria-labelledby="event-schedule-heading"
    >
      <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <header className="mb-10 text-center md:mb-12">
          <h2
            id="event-schedule-heading"
            className="relative mb-5 inline-block text-4xl font-semibold tracking-tight text-primary dark:text-primary-light md:text-5xl"
          >
            {t("eventSchedule.title")}
            <span
              className="absolute -bottom-2.5 left-1/2 h-0.5 w-16 -translate-x-1/2 rounded-full bg-primary dark:bg-primary-light"
              aria-hidden
            />
          </h2>
          <p className="mx-auto mt-5 max-w-3xl text-pretty text-lg leading-relaxed text-slate-600 dark:text-slate-400">
            {t("eventSchedule.description")}
          </p>
          {exportableCount > 0 && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={handleExportAll}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50 dark:bg-primary-light dark:hover:bg-primary dark:focus-visible:ring-primary-light/40 dark:focus-visible:ring-offset-slate-900 md:px-6 md:py-3"
              >
                <i className="fas fa-calendar-alt text-sm" aria-hidden />
                <span className="hidden sm:inline">
                  {t("button.export_all_events") ||
                    "Alle Events zum Kalender hinzufügen"}
                </span>
                <span className="sm:hidden">
                  {t("button.export_calendar") || "Kalender"}
                </span>
              </button>
            </div>
          )}
        </header>

        <div className="relative">
          <div
            aria-hidden
            className="pointer-events-none absolute bottom-2 left-5 top-10 z-0 w-px -translate-x-1/2 rounded-full bg-primary/30 dark:bg-primary-light/35 md:left-[1.375rem]"
          />
          <ol className="relative z-[1] m-0 flex list-none flex-col gap-8 p-0 md:gap-10">
            {allDisplayedItems.map((item, index) => (
              <li
                key={`${index}-${item.startISO ?? "noiso"}-${item.title}-${item.date}`}
                className="m-0 min-w-0 p-0"
              >
                <TimelineItem
                  {...item}
                  onExport={handleExportSingle}
                  t={t}
                />
              </li>
            ))}
          </ol>
        </div>

        {allTimelineItems.length > 3 && (
          <div className="mt-10 flex justify-center">
            <button
              type="button"
              onClick={() => setShowAdditionalDays((prev) => !prev)}
              aria-expanded={showAdditionalDays}
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-dark focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50 dark:bg-primary-light dark:hover:bg-primary dark:focus-visible:ring-primary-light/40 dark:focus-visible:ring-offset-slate-900 md:px-6 md:py-3"
            >
              {showAdditionalDays ? (
                <>
                  {t("button.view_less")}
                  <i className="fas fa-chevron-up" aria-hidden />
                </>
              ) : (
                <>
                  {t("button.view_more")}
                  <i className="fas fa-chevron-down" aria-hidden />
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

export default React.memo(EventSchedule);
